import { cn } from 'utils/helpers';
import { formatCurrency, TotalType, SUBTOTAL_ROW, TOTAL_ROWS, calculateTotalFromRows } from './utils';
import type { QuoteTableRow } from './quote-table.types';

export interface QuoteTableViewProps {
    className?: string;
    diffKeys?: Set<string>;
    hoveredRowIndex?: number | null;
    muted?: boolean;
    rows: QuoteTableRow[];
    showAllTotals?: boolean;
    showSubTotal?: boolean;
    showUnitColumn?: boolean;
    onRowHover?: (index: number | null) => void;
}

const HEADERS_WITH_UNIT = [
    { name: '#', width: '3%' },
    { name: 'Description of Scope', width: '40%' },
    { name: 'Unit of Measurement', width: '19%' },
    { name: 'Quantity', width: '12%' },
    { name: 'Rate', width: '12%' },
    { name: 'Cost', width: '12%' },
];

const HEADERS_WITHOUT_UNIT = [
    { name: '#', width: '5%' },
    { name: 'Description', width: '35%' },
    { name: 'Quantity', width: '20%' },
    { name: 'Rate', width: '20%' },
    { name: 'Cost', width: '20%' },
];

const COLUMN_STYLE = 'pl-2 font-roboto text-sm';

const QuoteTableView: React.FC<QuoteTableViewProps> = ({
    rows,
    diffKeys,
    hoveredRowIndex,
    showUnitColumn = false,
    showAllTotals = false,
    showSubTotal = false,
    muted = false,
    className,
    onRowHover,
}) => {
    const headers = showUnitColumn ? HEADERS_WITH_UNIT : HEADERS_WITHOUT_UNIT;

    const textClass = muted ? 'text-gray-500' : 'text-gray-900';
    const headerBg = muted ? 'bg-[#F3F4F6]' : 'bg-gray-200';
    const headerTextClass = muted ? 'text-gray-400' : 'text-tertiary-600';
    const tableBg = muted ? 'bg-gray-50' : 'bg-white';
    const cellBorder = 'border border-[#EAECF0]';

    // Total rows: spacer spans columns before Quantity, label spans Quantity + Rate, value is Cost
    const spacerColSpan = headers.length - 3;
    const labelColSpan = 2;

    return (
        <table
            className={cn(
                'w-full table-fixed rounded-xl border-b border-solid border-[#EAECF0]',
                tableBg,
                className
            )}
            data-testid="quote-table-view"
        >
            <thead className={cn('h-[44px]', headerBg)}>
                <tr className="border-b border-[#EAECF0]">
                    {headers.map((th, index) => (
                        <th
                            key={th.name}
                            className={cn(
                                headerBg,
                                'font-roboto text-[12px] font-medium',
                                headerTextClass,
                                index === 0 ? 'text-center' : 'text-left'
                            )}
                            style={{ width: th.width }}
                        >
                            <div className={cn(index === 0 ? 'ml-0' : "md:ml-1 lg:ml-1", "mt-[3px]")}>{th.name}</div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, idx) => {
                    const isQtyDiff = diffKeys?.has(`q-${row.itemId}.quantity`) ?? false;
                    const isRateDiff = diffKeys?.has(`q-${row.itemId}.rate`) ?? false;
                    const isCostDiff = diffKeys?.has(`q-${row.itemId}.cost`) ?? false;
                    const diffHighlight = 'bg-blue-50 font-medium text-blue-900 ring-2 ring-inset ring-blue-300';

                    const backgroundColor = hoveredRowIndex === idx ? 'bg-blue-100' : '';

                    return (
                        <tr
                            key={row.itemId}
                            className={cn(
                                'border-b border-[#EAECF0] transition-colors',
                                backgroundColor
                            )}
                            onMouseEnter={() => onRowHover?.(idx)}
                            onMouseLeave={() => onRowHover?.(null)}
                        >
                            <td
                                className={cn(
                                    cellBorder,
                                    'text-center font-roboto text-xs font-normal text-primary-500'
                                )}
                                style={{ height: '35px' }}
                            >
                                {idx + 1}
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    COLUMN_STYLE,
                                    textClass
                                )}
                            >
                                {row.name}
                            </td>
                            {showUnitColumn && (
                                <td
                                    className={cn(
                                        cellBorder,
                                        COLUMN_STYLE,
                                        textClass
                                    )}
                                >
                                    {row.unitLabel ?? '-'}
                                </td>
                            )}
                            <td
                                className={cn(
                                    cellBorder,
                                    COLUMN_STYLE,
                                    isQtyDiff ? diffHighlight : textClass
                                )}
                                data-testid={`view-qty-${row.itemId}`}
                            >
                                {row.quantity}
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    COLUMN_STYLE,
                                    isRateDiff ? diffHighlight : textClass
                                )}
                                data-testid={`view-rate-${row.itemId}`}
                            >
                                {formatCurrency(row.rate)}
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    COLUMN_STYLE,
                                    isCostDiff ? diffHighlight : textClass
                                )}
                                data-testid={`view-cost-${row.itemId}`}
                            >
                                {formatCurrency(row.quantity * row.rate)}
                            </td>
                        </tr>
                    );
                })}
                {showAllTotals &&
                    TOTAL_ROWS.map((total) => {
                        const isTotalDiff = diffKeys?.has(`total.${total.id}`) ?? false;
                        return (
                            <tr key={total.id}>
                                <td
                                    colSpan={spacerColSpan}
                                    className="h-[48px] border-none"
                                    style={{ background: '#F9FAFB' }}
                                />
                                <td
                                    colSpan={labelColSpan}
                                    className={cn(cellBorder, 'bg-gray-utility-100 px-4 py-2')}
                                    style={{ color: '#D0D5DD' }}
                                >
                                    <div
                                        className="font-roboto font-medium"
                                        style={{ fontSize: '12px', color: total.color }}
                                    >
                                        {total.label}
                                    </div>
                                </td>
                                <td
                                    className={cn(
                                        cellBorder,
                                        COLUMN_STYLE,
                                        isTotalDiff
                                            ? 'bg-blue-50 font-medium text-blue-900 ring-2 ring-inset ring-blue-300'
                                            : 'bg-gray-utility-100'
                                    )}
                                    style={isTotalDiff ? undefined : { color: '#333' }}
                                    data-testid={`view-total-${total.id}`}
                                >
                                    {formatCurrency(calculateTotalFromRows(rows, total.id))}
                                </td>
                            </tr>
                        );
                    })}
                {showSubTotal && !showAllTotals &&
                    <tr key={SUBTOTAL_ROW.id}>
                        <td
                            colSpan={spacerColSpan}
                            className="h-[48px] border-none"
                            style={{ background: '#F9FAFB' }}
                        />
                        <td
                            colSpan={labelColSpan}
                            className={cn(cellBorder, 'bg-gray-utility-100 px-4 py-2')}
                            style={{ color: '#D0D5DD' }}
                        >
                            <div
                                className="font-roboto font-medium"
                                style={{ fontSize: '12px', color: SUBTOTAL_ROW.color }}
                            >
                                {SUBTOTAL_ROW.label}
                            </div>
                        </td>
                        <td
                            className={cn(cellBorder, 'bg-gray-utility-100 px-4 py-2')}
                            style={{ color: '#333' }}
                        >
                            {formatCurrency(calculateTotalFromRows(rows, SUBTOTAL_ROW.id))}
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    );
};

export { QuoteTableView };
